import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  Checkbox,
  ListItemText,
  MenuItem,
  TextField,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  IDoesFilterPassParams,
  IFilterComp,
} from 'ag-grid-community';
import { getRolesService } from '../../services/roles.service';

const RoleFilter = forwardRef<IFilterComp, any>((props, ref) => {
  const theme = useTheme();
  const { onUiChange, onModelChange } = props;
  const organisationId = props.organisationId as string;

  const [roles, setRoles] = useState<any[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const guiRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getRolesService(organisationId).then(r => {
      setRoles(r.data.filter((x: any) => !x.hide));
    });
  }, [organisationId]);

  useImperativeHandle(ref, () => ({
    doesFilterPass(params: IDoesFilterPassParams) {
      return selected.length === 0 || selected.includes(params.data.userInfo.role_name);
    },
    isFilterActive() {
      return selected.length > 0;
    },
    getModel() {
      return { values: selected };
    },
    setModel(model: any) {
      setSelected(model?.values || []);
    },
    getGui() {
      return guiRef.current!;
    },
  }));

  const toggleRole = (roleId: string) => {
    const next = selected.includes(roleId)
      ? selected.filter(r => r !== roleId)
      : [...selected, roleId];
    setSelected(next);
    onModelChange?.({ values: next });
    onUiChange?.();
  };

  const display = roles.filter(r =>
    r.role_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={guiRef} style={{ position: 'relative' }}>
      <div
        style={{
          position: 'fixed',
          zIndex: theme.zIndex.modal,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: theme.shape.borderRadius,
          width: 200,
          padding: theme.spacing(1),
          color: theme.palette.text.primary,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search roles…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          variant="outlined"
          sx={{
            mb: 1,
            bgcolor: theme.palette.background.default,
            '& .MuiInputBase-input': {
              color: theme.palette.text.primary,
            }
          }}
        />
        {display.map(r => (
          <MenuItem
            key={r.role_id}
            dense
            onClick={() => toggleRole(r.role_id)}
            sx={{
              bgcolor: theme.palette.background.paper,
              '&.Mui-selected, &:hover': {
                bgcolor: theme.palette.action.hover,
              }
            }}
          >
            <Checkbox
              checked={selected.includes(r.role_id)}
              sx={{
                color: theme.palette.text.secondary,
                '&.Mui-checked': {
                  color: theme.palette.primary.main,
                }
              }}
            />
            <ListItemText
              primary={r.role_name}
              primaryTypographyProps={{
                variant: 'body2',
                color: theme.palette.text.primary,
              }}
            />
          </MenuItem>
        ))}
      </div>
    </div>
  );
});

export default RoleFilter;
